import { useEffect, useMemo } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { pass, vec4, vec3, uniform, hue, saturation, mix, vec2, length, smoothstep, screenUV, float } from 'three/tsl'
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js'
import { useControls } from 'leva'

// Post-processing for the WebGPU path, built on three's TSL display nodes.
//
// @react-three/postprocessing wraps the `postprocessing` package, which has zero WebGPU support —
// its EffectComposer calls getContext().getContextAttributes(), a WebGL-only API that throws and
// takes the Canvas down. This is the node-pipeline equivalent: a THREE.PostProcessing instance
// driving a scene pass through bloom and ACES tone mapping.
//
// Scope is narrower than the WebGL stack (no Noise or Sepia): bloom, tone mapping, and a
// saturation/contrast/brightness/warmth/hue grade with vignette.

// Leva string -> three tone-mapping constant.
const TONE_MAPPING_MODES = {
  None: THREE.NoToneMapping,
  Linear: THREE.LinearToneMapping,
  Reinhard: THREE.ReinhardToneMapping,
  Cineon: THREE.CineonToneMapping,
  ACESFilmic: THREE.ACESFilmicToneMapping,
  AgX: THREE.AgXToneMapping,
  Neutral: THREE.NeutralToneMapping,
}

// Gate before the inner component mounts, so its useControls call never registers the
// "Post Processing (WebGPU)" Leva panel on the WebGL path — where none of its sliders apply.
export default function PostProcessingWebGPU() {
  const isWebGPU = useThree(state => !!state.gl?.isWebGPURenderer)
  if (!isWebGPU) return null
  return <PostProcessingWebGPUInner />
}

function PostProcessingWebGPUInner() {
  const { gl, scene, camera, size } = useThree()

  const {
    enabled,
    bloomEnabled,
    bloomStrength,
    bloomRadius,
    bloomThreshold,
    toneMappingMode,
    exposure,
    saturationAmount,
    contrastAmount,
    brightnessAmount,
    warmthAmount,
    hueShift,
    vignetteAmount,
  } = useControls('Post Processing (WebGPU)', {
    // Unmounts the whole chain: SceneManager then renders the scene straight to the screen
    // instead of through the composer. Useful A/B for "is post-processing doing this?"
    enabled: { value: true, label: 'Master Enable' },

    bloomEnabled: { value: true, label: 'Bloom' },
    bloomStrength: { value: 0.13, min: 0, max: 3, step: 0.01, label: 'Bloom Strength' },
    bloomRadius: { value: 0.24, min: 0, max: 1, step: 0.01, label: 'Bloom Radius' },
    bloomThreshold: { value: 0.45, min: 0, max: 1, step: 0.01, label: 'Bloom Threshold' },

    // ACESFilmic, chosen by eye against Cineon (what the WebGL path ships). The two curves are
    // genuinely different — the WebGPU look is its own grade, not a match to WebGL.
    toneMappingMode: {
      value: 'ACESFilmic',
      options: ['None', 'Linear', 'Reinhard', 'Cineon', 'ACESFilmic', 'AgX', 'Neutral'],
      label: 'Tone Mapping',
    },
    exposure: { value: 1.75, min: 0, max: 3, step: 0.01, label: 'Exposure' },

    // Replacement for the colour grade that lived on the RTT ScreenPlane. Values below were
    // dialled in by Luke against the live WebGPU scene, 2026-07-21 — they are the approved look,
    // not neutral defaults. Change them only with a visual check.
    saturationAmount: { value: 1.44, min: 0, max: 2, step: 0.01, label: 'Saturation' },
    contrastAmount: { value: 1.0, min: 0, max: 2, step: 0.01, label: 'Contrast' },
    brightnessAmount: { value: 0.0, min: -0.5, max: 0.5, step: 0.01, label: 'Brightness' },
    warmthAmount: { value: 0.02, min: -0.2, max: 0.2, step: 0.001, label: 'Warmth' },
    hueShift: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Hue' },

    vignetteAmount: { value: 0.67, min: 0, max: 1, step: 0.01, label: 'Vignette' },
  }, { collapsed: false })

  const post = useMemo(() => {
    if (!gl?.isWebGPURenderer) return null

    // RenderPipeline is the current name; THREE.PostProcessing is a deprecated alias in r185.
    const Pipeline = THREE.RenderPipeline ?? THREE.PostProcessing
    const postProcessing = new Pipeline(gl)

    // Scene pass -> bloom -> add back over the original -> ACES + sRGB output encoding.
    const scenePass = pass(scene, camera)
    const scenePassColor = scenePass.getTextureNode('output')

    const bloomPass = bloom(scenePassColor, bloomStrength, bloomRadius, bloomThreshold)

    // Uniforms held outside the graph so the sliders update live without recompiling it.
    const bloomMix = uniform(1)
    const satUniform = uniform(saturationAmount)
    const hueUniform = uniform(hueShift)
    const vignetteUniform = uniform(vignetteAmount)
    const contrastUniform = uniform(contrastAmount)
    const brightnessUniform = uniform(brightnessAmount)
    const warmthUniform = uniform(warmthAmount)
    // Exposure is applied in the graph, not via renderer.toneMappingExposure — RenderPipeline
    // overrides renderer state during its pass, so setting it there had no effect.
    const exposureUniform = uniform(exposure)

    // Add the glow to RGB only, keeping the scene's own alpha.
    //
    // bloom() returns just the extracted glow, so adding it to the scene is correct — but a
    // plain vec4 .add() sums the alpha channel too, pushing it past 1 and lifting the whole
    // image. That reads as "washed out" as soon as bloom is switched on.
    const withBloom = scenePassColor.rgb.add(bloomPass.rgb.mul(bloomMix))

    // ORDER MATTERS, and getting it wrong is what made this unusable to tune.
    //
    // The scene pass is LINEAR, scene-referred HDR — values are not bounded to 0..1. Grading
    // there is meaningless: a contrast pivot around 0.5 assumes mid-grey sits at 0.5, and
    // saturation()/hue() assume display-referred input. Applied to linear HDR they blow out or
    // crush unpredictably, which is exactly what "I can't get it to look good" felt like.
    //
    // So: exposure in linear (physically correct), then tone map to display-referred 0..1,
    // and only then grade. This is the standard order and matches what the old RTT plane did —
    // it graded the already-tone-mapped texture, not the raw scene.
    // DO NOT call renderOutput() here.
    //
    // RenderPipeline applies tone mapping and colour-space conversion to whatever outputNode
    // produces — automatically, as its final step. Calling renderOutput() ourselves tone-maps
    // and sRGB-encodes a SECOND time, which is what made the image washed out and bright
    // regardless of bloom, exposure or any grade setting.
    //
    // The pipeline reads renderer.toneMapping / renderer.outputColorSpace for that final step,
    // so the tone-mapping mode is set on the renderer (see the effect below), not passed here.
    //
    // Everything in this graph therefore stays in LINEAR space and ends un-encoded.
    const exposed = withBloom.mul(exposureUniform)

    const hueSat = saturation(hue(exposed, hueUniform), satUniform)
    const contrasted = hueSat.sub(0.5).mul(contrastUniform).add(0.5)
    const brightened = contrasted.add(brightnessUniform)

    // Warmth: positive lifts red and drops blue (towards tungsten), negative the reverse.
    const graded = brightened.add(
      vec3(warmthUniform, float(0), warmthUniform.negate()),
    )

    // Radial darkening from the screen centre. screenUV is 0..1 across the viewport.
    const dist = length(screenUV.sub(vec2(0.5, 0.5)))
    const vignetteFactor = mix(
      float(1),
      smoothstep(0.8, 0.25, dist),
      vignetteUniform,
    )

    postProcessing.outputNode = vec4(graded.mul(vignetteFactor), scenePassColor.a)

    // PostProcessing is not an Object3D, so it has no userData — return the tunable nodes
    // alongside it so the Leva controls can update uniforms without rebuilding the graph.
    return {
      postProcessing, bloomPass, bloomMix,
      satUniform, hueUniform, vignetteUniform,
      contrastUniform, brightnessUniform, warmthUniform, exposureUniform,
    }
    // Rebuilt only when the renderer/scene/camera identity changes — the tunables below are
    // uniforms, so changing them must not recompile the node graph.
    // toneMappingMode is baked into the node graph so it must rebuild; the numeric sliders flow
    // through uniforms and deliberately do NOT trigger a recompile.
  }, [gl, scene, camera, toneMappingMode])

  // Live uniform updates.
  useEffect(() => {
    const b = post?.bloomPass
    if (!b) return
    b.strength.value = bloomStrength
    b.radius.value = bloomRadius
    b.threshold.value = bloomThreshold
  }, [post, bloomStrength, bloomRadius, bloomThreshold])

  // Toggling bloom multiplies its contribution to zero rather than rebuilding the graph.
  useEffect(() => {
    if (post?.bloomMix) post.bloomMix.value = bloomEnabled ? 1 : 0
  }, [post, bloomEnabled])

  useEffect(() => {
    if (!post) return
    post.satUniform.value = saturationAmount
    post.hueUniform.value = hueShift
    post.vignetteUniform.value = vignetteAmount
    post.contrastUniform.value = contrastAmount
    post.brightnessUniform.value = brightnessAmount
    post.warmthUniform.value = warmthAmount
  }, [post, saturationAmount, hueShift, vignetteAmount, contrastAmount, brightnessAmount, warmthAmount])

  useEffect(() => {
    if (post?.exposureUniform) post.exposureUniform.value = exposure
  }, [post, exposure])

  // RenderPipeline reads renderer.toneMapping for the automatic final step, so the mode is set
  // here rather than baked into the node graph. app.jsx leaves the renderer on NoToneMapping —
  // correct for the WebGL path, but on WebGPU it would mean no tone mapping at all.
  useEffect(() => {
    if (!gl?.isWebGPURenderer) return
    gl.toneMapping = enabled
      ? (TONE_MAPPING_MODES[toneMappingMode] ?? THREE.CineonToneMapping)
      : THREE.NoToneMapping
    return () => { gl.toneMapping = THREE.NoToneMapping }
  }, [gl, toneMappingMode, enabled])

  // Drive the composed output instead of R3F's plain render.
  //
  // SceneManager's priority-1 useFrame is what renders on the WebGPU path (a useFrame with a
  // priority puts R3F into manual render mode). Rather than fight it for ownership of the frame,
  // this sets a flag that SceneManager checks — when post-processing is active it calls
  // post.renderAsync() instead of gl.render().
  useEffect(() => {
    if (!gl?.isWebGPURenderer) return
    gl.userData = gl.userData || {}
    gl.userData.postProcessing = (enabled && post) ? post.postProcessing : null
    return () => {
      if (gl.userData) gl.userData.postProcessing = null
    }
  }, [gl, post, enabled])

  // Keep the composer's internal targets in step with the canvas.
  useEffect(() => {
    post?.postProcessing?.setSize?.(size.width, size.height)
  }, [post, size.width, size.height])

  useEffect(() => () => { post?.postProcessing?.dispose?.() }, [post])


  return null
}
